<template>
  <v-container fluid class="pr-5 py-2 pl-0" style="background-color: var(--v-background-base) !important">
    <ExpansionPanel :show-expand-icon="false" :show-actions="false" :always-visible-values="alwaysVisibleFiltersKey" @on-clear="clearFilters" @on-search="searchData">
      <template #always-visible>
        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="approvalFinancialGroup"
            :fetch="fetchFinancialGroups"
            :refresh-list-items="refreshFinancialGroups"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.financialGroupRequired')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setApprovalFilter('financialGroup', $event)"
            @listUpdated="refreshFinancialGroups = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="approvalCarrier"
            :fetch="fetchCarriers"
            :refresh-list-items="refreshCarriers"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.carrier')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :disabled="!approvalFinancialGroup"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setApprovalFilter('carrier', $event)"
            @listUpdated="refreshCarriers = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="approvalContract"
            :fetch="fetchContracts"
            :refresh-list-items="refreshContracts"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.filters.contract')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :disabled="!approvalFinancialGroup"
            :min-search-chars="0"
            :page-size="10"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setApprovalFilter('contract', $event)"
            @listUpdated="refreshContracts = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-menu ref="competencyMenu" v-model="competencyMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
            <template #activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="width: 100%">
                <Textfield :value="approvalCompetencyLabel" v-bind="textfieldAttrs" readonly clearable hide-details style="width: 100%" :label="$t('biControlPanel.filters.competency')" @click:clear.stop="clearApprovalCompetency">
                  <template #append>
                    <v-icon size="20" color="primary"> mdi-calendar-month </v-icon>
                  </template>
                </Textfield>
              </div>
            </template>

            <v-date-picker v-model="competencyDate" type="month" locale="pt-br" color="primary" no-title scrollable @change="setApprovalCompetency">
              <v-spacer />

              <v-btn text color="primary" @click="setApprovalCompetency">
                {{ $t("common.confirm") }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </template>
    </ExpansionPanel>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </v-container>
</template>

<script>
import moment from 'moment';
import ExpansionPanel from '@/components/BIControlPanel/ExpansionPanel.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import Textfield from '@/components/Default/Textfield.vue';
import { AUTOCOMPLETE_ATTRS, TEXTFIELD_ATTRS } from '@/shared/constants/admissionMap/DefaultConstants';
import BIControlPanelModule from '@/store/modules/bi-control-panel/bi-control-panel-module';

moment.locale('pt-br');

export default {
  name: 'BIApprovalFilter',
  components: {
    ExpansionPanel,
    SnackbarCustomize,
    PaginatedAutocomplete,
    Textfield,
  },
  data() {
    return {
      competencyMenu: false,
      competencyDate: null,
      refreshFinancialGroups: false,
      refreshContracts: false,
      refreshCarriers: false,
    };
  },
  computed: {
    approvalFilter() {
      return BIControlPanelModule.approvalFilter;
    },
    approvalFilterOptions() {
      return BIControlPanelModule.approvalFilterOptions;
    },
    alwaysVisibleFiltersKey() {
      return [this.approvalFilter.financialGroup || '', this.approvalFilter.contract || '', this.approvalFilter.carrier || '', this.approvalFilter.competency || ''].join('|');
    },
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
    textfieldAttrs() {
      return {
        ...TEXTFIELD_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
    approvalCompetencyLabel() {
      if (!this.approvalFilter.competency) {
        return null;
      }

      const competency = moment(this.approvalFilter.competency, 'YYYY-MM', true);

      if (!competency.isValid()) {
        return this.approvalFilter.competency;
      }

      const monthName = competency.format('MMMM');
      const monthCapitalized = monthName.charAt(0).toUpperCase() + monthName.slice(1);

      return `${monthCapitalized} ${competency.format('YYYY')}`;
    },
    approvalFinancialGroup: {
      get() {
        return this.approvalFilter.financialGroup;
      },
      set(value) {
        BIControlPanelModule.setApprovalFilterValue({
          fieldKey: 'financialGroup',
          value: value || null,
        });
      },
    },
    approvalContract: {
      get() {
        return this.approvalFilter.contract;
      },
      set(value) {
        BIControlPanelModule.setApprovalFilterValue({
          fieldKey: 'contract',
          value: value || null,
        });
      },
    },
    approvalCarrier: {
      get() {
        return this.approvalFilter.carrier;
      },
      set(value) {
        BIControlPanelModule.setApprovalFilterValue({
          fieldKey: 'carrier',
          value: value || null,
        });
      },
    },
    approvalCompetency: {
      get() {
        return this.approvalFilter.competency;
      },
      set(value) {
        BIControlPanelModule.setApprovalFilterValue({
          fieldKey: 'competency',
          value: value || null,
        });
      },
    },
  },
  watch: {
    'approvalFilter.competency': {
      immediate: true,
      handler(value) {
        this.competencyDate = value || null;
      },
    },
  },
  mounted() {
    this.runStoreAction(
      BIControlPanelModule.initializeApprovalTab(),
      this.translate('biControlPanel.messages.approvalPanelLoadError'),
    );
  },
  methods: {
    emptyFilterOptionsResponse(page = 0, size = 20) {
      return {
        content: [],
        page,
        size,
        totalElements: 0,
        totalPages: 0,
        last: true,
      };
    },
    fetchFinancialGroups(page, size, searchText) {
      return this.fetchPaginatedFilterOptions('financialGroup', page, size, searchText);
    },
    fetchContracts(page, size, searchText) {
      if (!this.approvalFinancialGroup) {
        return Promise.resolve(this.emptyFilterOptionsResponse(page, size));
      }

      return this.fetchPaginatedFilterOptions('contract', page, size, searchText);
    },
    fetchCarriers(page, size, searchText) {
      if (!this.approvalFinancialGroup) {
        return Promise.resolve(this.emptyFilterOptionsResponse(page, size));
      }

      return this.fetchPaginatedFilterOptions('carrier', page, size, searchText);
    },
    async fetchPaginatedFilterOptions(fieldKey, page, size, searchText) {
      const result = await this.runStoreAction(
        BIControlPanelModule.searchApprovalFilterOptions({
          fieldKey,
          reset: page === 0,
          page,
          size,
          searchText: searchText || '',
        }),
        this.translate('biControlPanel.messages.filterOptionsLoadError'),
      );

      if (!result || result.success === false || !result.data) {
        return this.emptyFilterOptionsResponse(page, size);
      }

      return result.data;
    },
    setApprovalFilter(fieldKey, value) {
      const normalizedValue = value || null;
      const previousValue = this.approvalFilter[fieldKey];

      BIControlPanelModule.setApprovalFilterValue({
        fieldKey,
        value: normalizedValue,
      });

      if (previousValue === normalizedValue) {
        return;
      }

      this.refreshDependentFilterLists(fieldKey);
    },
    refreshDependentFilterLists(fieldKey) {
      const fieldsToRefresh = {
        financialGroup: ['contract', 'carrier'],
        contract: ['carrier'],
        carrier: [],
        competency: [],
      }[fieldKey] || [];

      fieldsToRefresh.forEach((field) => {
        this.triggerListRefresh(`refresh${this.capitalize(field)}s`);
      });
    },
    triggerListRefresh(flagName) {
      if (!Object.prototype.hasOwnProperty.call(this.$data, flagName)) {
        return;
      }

      this[flagName] = false;

      this.$nextTick(() => {
        this[flagName] = true;
      });
    },
    capitalize(value) {
      return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    },
    handleFilterOptionsError() {
      this.showError(this.translate('biControlPanel.messages.filterOptionsLoadError'));
    },
    setApprovalCompetency(value) {
      const nextValue = typeof value === 'string' ? value : this.competencyDate;

      if (nextValue) {
        this.competencyDate = nextValue;
        this.setApprovalFilter('competency', nextValue);
      }

      this.competencyMenu = false;
    },
    clearApprovalCompetency() {
      this.competencyDate = null;
      this.setApprovalFilter('competency', null);
      this.competencyMenu = false;
    },
    refreshFilterLists() {
      ['refreshFinancialGroups', 'refreshContracts', 'refreshCarriers'].forEach((flagName) => this.triggerListRefresh(flagName));
    },
    async clearFilters() {
      const clearResult = await this.runStoreAction(
        BIControlPanelModule.clearApprovalFilters(),
        this.translate('biControlPanel.messages.filtersClearError'),
      );

      if (!clearResult || clearResult.success === false) {
        return;
      }

      this.refreshFilterLists();
    },
    async searchData() {
      await this.runStoreAction(
        BIControlPanelModule.submitApprovalFilters(),
        this.translate('biControlPanel.messages.dataSearchError'),
      );
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>
